import { faAdd, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import { Goal } from "../../../types/goal";
import useApp from "../../../utils/useApp";
import {
  deleteGoalTemplate,
  deleteTaskTemplate,
  patchGoalTemplate,
  patchTaskTemplate,
  postGoalCategory,
  postTaskTemplate,
  reorderTaskTemplate,
} from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import IconButton from "../../ui/IconButton";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import ReorderEditDeleteCards from "../../ui/ReorderEditDeleteCards";
import SelectObjectTypeahead from "../../ui/SelectObjectTypeahead";

interface GoalTemplateState {
  goal: Goal;
  getGoalTemplates: any;
  getGoalCategories: any;
  setSearchText: any;
  categories: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setErrMsg: any;
}

const GoalTemplateCard = ({
  goal,
  getGoalTemplates,
  getGoalCategories,
  setSearchText,
  categories,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setErrMsg,
}: GoalTemplateState) => {
  const { auth }: any = useApp();
  const id = auth?.profile?.userOrgID;
  const [isEditing, setIsEditing] = useState(false);
  const [isTaskEditing, setIsTaskEditing] = useState(false);
  const [allowSaveGoal, setAllowSaveGoal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<Goal>();
  const [goalName, setGoalName] = useState<string>(goal?.name);
  const [category, setCategory] = useState<any>(null);
  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [newTask, setNewTask] = useState<string>("");
  const [allowSaveTask, setAllowSaveTask] = useState<boolean>(false);

  useEffect(() => {
    setGoalName(goal?.name);
    setCategory(goal?.category_key);
  }, [goal]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
    setGoalToDelete(goal);
  };

  const onClickDeleteFinal = () => {
    setErrMsg("");
    deleteGoalTemplate(auth?.profile?.userOrgID, goalToDelete?.id)
      .then((res: any) => {
        setIsDeleting(false);
        setGoalToDelete(undefined);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Goal Template Deleted");
        setToastBody(goalToDelete?.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setIsEditing(false);
    setIsTaskEditing(false);
    setGoalToDelete(undefined);
  };

  const onClickSave = () => {
    if (!goalName) {
      setErrMsg("Cannot set null goal name");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }

    const updatedGoal = category ? { name: goalName, category_id: category?.id !== "0" ? category?.id : null } : { name: goalName };
    setErrMsg("");

    patchGoalTemplate(updatedGoal, auth?.profile?.userOrgID, goal?.id)
      .then((res: any) => {
        setIsEditing(false);
        setAllowSaveGoal(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        setSearchText("");
        getGoalTemplates(false);
        setShowToast(true);
        setToastText("Goal Template Updated");
        setToastBody(goalName);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancel = () => {
    setErrMsg("");
    setIsEditing(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const updateGoalName = (e: any) => {
    setGoalName(e.target.value);
    setAllowSaveGoal(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateCategory = (e: any) => {
    setCategory(e);
    setAllowSaveGoal(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickAddCategory = async (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postGoalCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        getGoalCategories();
        const cat = `${id}_${newCategory.toLowerCase()}`;
        setCategory(cat);
        return { key: cat, name: newCategory, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickAddTask = () => {
    setShowAddTask(true);
  };

  const updateTask = (e: any) => {
    setNewTask(e.target.value);
    setAllowSaveTask(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickSaveTask = () => {
    const task = {
      name: newTask,
    };

    setErrMsg("");

    postTaskTemplate(task, auth?.profile?.userOrgID, goal?.id)
      .then((res: any) => {
        setShowAddTask(false);
        setNewTask("");
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Task Created");
        setToastBody(newTask);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelTask = () => {
    setShowAddTask(false);
    setNewTask("");
    setAllowSaveTask(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickGoalSubmit = (e: any) => {
    e?.preventDefault();
    onClickSave();
  };

  const onClickTaskSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveTask();
  };

  const onClickEditTaskFinal = (taskId: any, taskName: string) => {
    const updatedTask = {
      name: taskName,
    };

    setErrMsg("");

    patchTaskTemplate(updatedTask, auth?.profile?.userOrgID, goal?.id, taskId)
      .then((res: any) => {
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        setIsTaskEditing(false);
        setIsEditing(false);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Task Updated");
        setToastBody(taskName);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickDeleteTaskFinal = (taskId: string, taskName: string) => {
    setErrMsg("");
    deleteTaskTemplate(auth?.profile?.userOrgID, goal?.id, taskId)
      .then((res: any) => {
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setShowToast(true);
        setToastText("Task Deleted");
        setToastBody(taskName);
        setIsDeleting(false);
        getGoalTemplates();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const updateTaskOrder = (newOrder: any) => {
    reorderTaskTemplate(auth?.profile?.userOrgID, goal?.id, newOrder)
      .then(() => {
        setShowToast(true);
        setToastText("Task Order");
        setToastBody(goal?.name);
        getGoalTemplates(false);
      })
      .catch((err) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to delete - {goalToDelete?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <Card className="bg-light p-3" key={goal?.id}>
          {isEditing ? (
            <Row className="mb-3">
              <Col xs={12}>
                <Form onSubmit={(e: any) => onClickGoalSubmit(e)}>
                  <Stack direction="horizontal" gap={3}>
                    <Col xs={6}>
                      <Input label="Goals" type="textarea" value={goalName} onUpdate={(e: any) => updateGoalName(e)} />
                    </Col>
                    <Col>
                      <SelectObjectTypeahead
                        initialOption="Search or type to create"
                        label="Category"
                        value={category}
                        options={categories}
                        onUpdate={(e: any) => updateCategory(e)}
                        allowCreate={true}
                        onCreateOption={async (inputValue: string) => {
                          const newCategory = await onClickAddCategory(inputValue);
                          return newCategory;
                        }}
                      />
                    </Col>
                    <Button className="button app-secondary-color button" onClick={() => onClickSave()} disabled={!allowSaveGoal}>
                      Save
                    </Button>
                    <div className="vr" />
                    <Button className="button app-primary-color button" onClick={() => onClickCancel()}>
                      Cancel
                    </Button>
                  </Stack>
                </Form>
              </Col>
            </Row>
          ) : (
            <Stack direction="horizontal" gap={3}>
              <Stack style={{ justifyContent: "center" }} className="col-6">
                <h5 className="app-orange-text">{goal.name}</h5>
              </Stack>
              <Badge bg="secondary">{goal.category_name}</Badge>
              {isEditing || isTaskEditing || showAddTask ? (
                <>
                  <FontAwesomeIcon className={"app-light-gray-text"} icon={faEdit} />
                  <div className="vr" />
                </>
              ) : (
                <>
                  <IconButton onClickButton={() => onClickEdit()} tooltipText="Edit Goal" icon={faEdit} />
                  <div className="vr" />
                </>
              )}
              <div>
                <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete Goal" icon={faTrash} />
              </div>
            </Stack>
          )}
          <Row>
            <h6>Tasks</h6>
          </Row>
          <Stack gap={2} className="mb-3">
            {goal?.tasks?.length > 0 && (
              <ReorderEditDeleteCards
                items={goal?.tasks}
                editInputLabel="Task"
                onEditFinal={onClickEditTaskFinal}
                onDeleteFinal={onClickDeleteTaskFinal}
                updateOrder={updateTaskOrder}
                setErrMessage={setErrMsg}
              />
            )}
          </Stack>
          <Row className="mb-0">
            {showAddTask ? (
              <Col xs={12}>
                <Form onSubmit={(e: any) => onClickTaskSubmit(e)}>
                  <Stack direction="horizontal" gap={3}>
                    <Stack style={{ justifyContent: "center" }}>
                      <Input label="Enter New Task" type="text" value={newTask} hasFocus={true} onUpdate={(e: any) => updateTask(e)} />
                    </Stack>
                    <Button className="app-secondary-color button" disabled={!allowSaveTask} onClick={() => onClickSaveTask()}>
                      Save
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickCancelTask()}>
                      Cancel
                    </Button>
                  </Stack>
                </Form>
              </Col>
            ) : (
              <Col xs={12} className="d-flex justify-content-end">
                <IconEmbeddedButton onClickButton={onClickAddTask} tooltipText="Add Task" icon={faAdd} />
              </Col>
            )}
          </Row>
        </Card>
      )}
    </>
  );
};

export default GoalTemplateCard;
